/**
 * "Por oferta": ranking das ofertas (produtos e planos) com o que entrou,
 * voltou e ainda esta parado, ordenavel por coluna, com busca e "ver mais".
 *
 * Decisoes nao obvias:
 *  1. Pensada para centenas de ofertas: mostra as 6 primeiras na ordem
 *     escolhida e um botao "Ver as outras N"; a busca recorta pelo nome sem
 *     acento; o filtro por funil so aparece quando a empresa tem os dois.
 *     Nunca renderiza a lista inteira sem o lojista pedir.
 *  2. Ordenacao padrao por VALOR PARADO: e a coluna que diz onde ainda ha
 *     dinheiro para buscar -- o que a referencia nao ordenava.
 *  3. Tres destaques em cima (maior valor parado, melhor taxa, pior taxa)
 *     poupam a leitura da tabela para quem so quer o ponto de atencao. Taxa
 *     so conta com 5 entradas ou mais: 1 de 1 nao e 100%, e sorte.
 *  4. Em tela estreita a tabela vira lista de cartoes (padrao de LeadsTable),
 *     nunca sete colunas espremidas.
 *  5. A linha de total soma o que esta FILTRADO, e diz isso, para o total
 *     nunca discordar das linhas visiveis.
 */

import { useId, useMemo, useState } from 'react';
import { ErrorPanel } from '../../../components/PageState';
import { Badge, Button, FilterChips, Icon, Input, SectionCard, type ChipOption } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents, formatCount, formatRatio, PROVIDER_LABEL, VALOR_AUSENTE } from '../../../lib/format';
import type { FunnelKind } from '../../../types';
import type { FunnelsSummary, OfferSummary } from '../types';

const LIMITE_INICIAL = 6;
const MINIMO_PARA_TAXA = 5;

type FiltroDeFunil = 'todos' | FunnelKind;
type Coluna = 'nome' | 'entraram' | 'recuperados' | 'taxa' | 'recuperado' | 'parado' | 'abertos';

interface Ordem {
  readonly coluna: Coluna;
  readonly desc: boolean;
}

interface ColunaInfo {
  readonly rotulo: string;
  readonly numerica: boolean;
  readonly titulo: string;
}

const COLUNAS: Readonly<Record<Coluna, ColunaInfo>> = {
  nome: { rotulo: 'Oferta', numerica: false, titulo: 'Produto ou plano, como veio da plataforma' },
  entraram: { rotulo: 'Entraram', numerica: true, titulo: 'Abandonaram o checkout ou tiveram a assinatura vencida no período' },
  recuperados: { rotulo: 'Recuperados', numerica: true, titulo: 'Pagaram em até 7 dias depois de um contato' },
  taxa: { rotulo: 'Taxa', numerica: true, titulo: 'Recuperados sobre os que entraram; só com 5 entradas ou mais' },
  recuperado: { rotulo: 'Recuperado', numerica: true, titulo: 'Valor pago pelos recuperados' },
  parado: { rotulo: 'Parado', numerica: true, titulo: 'Valor de quem entrou e ainda não teve desfecho' },
  abertos: { rotulo: 'Em aberto', numerica: true, titulo: 'Pessoas que ainda dão para recuperar' },
};

const ORDEM_DAS_COLUNAS: readonly Coluna[] = ['nome', 'entraram', 'recuperados', 'taxa', 'recuperado', 'parado', 'abertos'];

const ROTULO_DO_FUNIL: Readonly<Record<FunnelKind, string>> = {
  carrinho_abandonado: 'Carrinho',
  expirados: 'Expirados',
};

function taxaBps(o: OfferSummary): number {
  return o.enteredCount === 0 ? 0 : Math.round((o.recoveredCount * 10_000) / o.enteredCount);
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

function valorDaColuna(o: OfferSummary, coluna: Coluna): number {
  switch (coluna) {
    case 'entraram':
      return o.enteredCount;
    case 'recuperados':
      return o.recoveredCount;
    case 'taxa':
      return o.enteredCount >= MINIMO_PARA_TAXA ? taxaBps(o) : -1;
    case 'recuperado':
      return o.recoveredAmountCents;
    case 'parado':
      return o.openAmountCents;
    case 'abertos':
      return o.openCount;
    case 'nome':
      return 0;
  }
}

function ordenar(lista: readonly OfferSummary[], ordem: Ordem): readonly OfferSummary[] {
  const sinal = ordem.desc ? -1 : 1;
  return [...lista].sort((a, b) => {
    if (ordem.coluna === 'nome') {
      return sinal * a.name.localeCompare(b.name, 'pt-BR');
    }
    const diff = valorDaColuna(a, ordem.coluna) - valorDaColuna(b, ordem.coluna);
    return diff === 0 ? a.name.localeCompare(b.name, 'pt-BR') : sinal * diff;
  });
}

export function DestaquesPorOferta({
  resumo,
  onRetry,
  funis,
  className = '',
}: {
  readonly resumo: AsyncState<FunnelsSummary>;
  readonly onRetry: () => void;
  readonly funis: readonly FunnelKind[];
  readonly className?: string;
}): JSX.Element {
  const [filtro, setFiltro] = useState<FiltroDeFunil>('todos');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>({ coluna: 'parado', desc: true });
  const [expandido, setExpandido] = useState(false);
  const idBusca = useId();

  const ofertas = resumo.status === 'success' ? resumo.data.offers : [];

  const filtradas = useMemo(() => {
    const termo = normalizar(busca);
    return ofertas.filter((o) => (filtro === 'todos' || o.kind === filtro) && (termo === '' || normalizar(o.name).includes(termo)));
  }, [ofertas, filtro, busca]);

  const ordenadas = useMemo(() => ordenar(filtradas, ordem), [filtradas, ordem]);
  const visiveis = expandido ? ordenadas : ordenadas.slice(0, LIMITE_INICIAL);
  const escondidas = ordenadas.length - visiveis.length;

  const chips: readonly ChipOption<FiltroDeFunil>[] = [
    { value: 'todos', label: 'Tudo', count: formatCount(ofertas.length) },
    ...funis.map((kind) => ({ value: kind, label: ROTULO_DO_FUNIL[kind], count: formatCount(ofertas.filter((o) => o.kind === kind).length) })),
  ];

  const alternarOrdem = (coluna: Coluna): void => {
    setOrdem((atual) => (atual.coluna === coluna ? { coluna, desc: !atual.desc } : { coluna, desc: coluna !== 'nome' }));
  };

  return (
    <SectionCard
      id="por-oferta"
      title="Por oferta"
      icon="estrela"
      description="Onde o abandono se concentra, o que voltou e onde ainda há valor parado. Clique no cabeçalho para ordenar."
      className={className}
      aside={
        <>
          {funis.length > 1 ? <FilterChips options={chips} value={filtro} onChange={setFiltro} label="Filtrar ofertas por funil" /> : null}
          <div className="relative">
            <label htmlFor={idBusca} className="sr-only">
              Buscar oferta pelo nome
            </label>
            <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
            <Input id={idBusca} size="sm" type="search" placeholder="Buscar oferta" value={busca} onChange={(e) => setBusca(e.target.value)} className="w-48 pl-9" />
          </div>
        </>
      }
    >
      {resumo.status === 'loading' ? <div aria-hidden="true" className="h-56 animate-pulse rounded-xl bg-ink/5" /> : null}
      {resumo.status === 'error' ? <ErrorPanel title="Não foi possível carregar as ofertas" error={resumo.error} onRetry={onRetry} /> : null}
      {resumo.status === 'success' ? (
        ofertas.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-subtle">Nenhuma oferta teve entrada no período: quando alguém abandonar um checkout ou deixar um plano vencer, ela aparece aqui.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <Destaques ofertas={filtradas} />
            {filtradas.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-subtle">Nenhuma oferta combina com «{busca.trim()}»{filtro === 'todos' ? '' : ' neste funil'}.</p>
            ) : (
              <>
                <Tabela ofertas={visiveis} total={filtradas} ordem={ordem} onOrdenar={alternarOrdem} mostrarFunil={filtro === 'todos' && funis.length > 1} />
                <Cartoes ofertas={visiveis} mostrarFunil={filtro === 'todos' && funis.length > 1} />
                {escondidas > 0 || expandido ? (
                  <div className="flex justify-center">
                    <Button variant="ghost" size="sm" icon="chevron" onClick={() => setExpandido((v) => !v)} className={expandido ? '[&>svg]:rotate-180' : ''}>
                      {expandido ? 'Ver menos' : `Ver as outras ${formatCount(escondidas)} ${escondidas === 1 ? 'oferta' : 'ofertas'}`}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )
      ) : null}
    </SectionCard>
  );
}

function Destaques({ ofertas }: { readonly ofertas: readonly OfferSummary[] }): JSX.Element | null {
  const maiorParado = ofertas.reduce<OfferSummary | null>((melhor, o) => (o.openAmountCents > 0 && (melhor === null || o.openAmountCents > melhor.openAmountCents) ? o : melhor), null);
  const comTaxa = ofertas.filter((o) => o.enteredCount >= MINIMO_PARA_TAXA);
  const melhorTaxa = comTaxa.reduce<OfferSummary | null>((melhor, o) => (melhor === null || taxaBps(o) > taxaBps(melhor) ? o : melhor), null);
  const piorTaxa = comTaxa.reduce<OfferSummary | null>((pior, o) => (pior === null || taxaBps(o) < taxaBps(pior) ? o : pior), null);
  if (maiorParado === null && melhorTaxa === null) {
    return null;
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-3">
      {maiorParado === null ? null : (
        <Destaque tone="accent" icone="moeda" rotulo="Maior valor parado" nome={maiorParado.name} valor={`${formatBRLFromCents(maiorParado.openAmountCents)} · ${formatCount(maiorParado.openCount)} em aberto`} />
      )}
      {melhorTaxa === null ? null : (
        <Destaque tone="good" icone="estrela" rotulo="Melhor taxa" nome={melhorTaxa.name} valor={`${formatRatio(melhorTaxa.recoveredCount, melhorTaxa.enteredCount)} de ${formatCount(melhorTaxa.enteredCount)}`} />
      )}
      {piorTaxa === null || piorTaxa.offerId === melhorTaxa?.offerId ? null : (
        <Destaque tone="critical" icone="alerta" rotulo="Pior taxa" nome={piorTaxa.name} valor={`${formatRatio(piorTaxa.recoveredCount, piorTaxa.enteredCount)} de ${formatCount(piorTaxa.enteredCount)}`} />
      )}
    </ul>
  );
}

const TOM_DO_DESTAQUE: Readonly<Record<'accent' | 'good' | 'critical', string>> = {
  accent: 'bg-accent/10 text-accent',
  good: 'bg-status-good/10 text-status-good',
  critical: 'bg-status-critical/10 text-status-critical',
};

function Destaque({
  tone,
  icone,
  rotulo,
  nome,
  valor,
}: {
  readonly tone: keyof typeof TOM_DO_DESTAQUE;
  readonly icone: 'moeda' | 'estrela' | 'alerta';
  readonly rotulo: string;
  readonly nome: string;
  readonly valor: string;
}): JSX.Element {
  return (
    <li className="flex min-w-0 items-start gap-3 rounded-xl border border-line bg-elevated px-3 py-3">
      <span aria-hidden="true" className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${TOM_DO_DESTAQUE[tone]}`}>
        <Icon name={icone} size="sm" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{rotulo}</p>
        <p className="truncate text-sm font-semibold text-ink" title={nome}>
          {nome}
        </p>
        <p className="text-xs tabular-nums text-ink-muted">{valor}</p>
      </div>
    </li>
  );
}

function Tabela({
  ofertas,
  total,
  ordem,
  onOrdenar,
  mostrarFunil,
}: {
  readonly ofertas: readonly OfferSummary[];
  readonly total: readonly OfferSummary[];
  readonly ordem: Ordem;
  readonly onOrdenar: (coluna: Coluna) => void;
  readonly mostrarFunil: boolean;
}): JSX.Element {
  const soma = total.reduce(
    (acc, o) => ({
      entraram: acc.entraram + o.enteredCount,
      recuperados: acc.recuperados + o.recoveredCount,
      recuperado: acc.recuperado + o.recoveredAmountCents,
      parado: acc.parado + o.openAmountCents,
      abertos: acc.abertos + o.openCount,
    }),
    { entraram: 0, recuperados: 0, recuperado: 0, parado: 0, abertos: 0 },
  );
  return (
    <div className="hidden overflow-x-auto rounded-xl border border-line bg-surface md:block">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Ofertas com entradas no período, recuperados, taxa, valor recuperado, valor parado e pessoas em aberto. Ordenável por coluna.</caption>
        <thead>
          <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
            {ORDEM_DAS_COLUNAS.map((coluna) => {
              const info = COLUNAS[coluna];
              const ativa = ordem.coluna === coluna;
              return (
                <th
                  key={coluna}
                  scope="col"
                  data-numeric={info.numerica ? 'true' : undefined}
                  aria-sort={ativa ? (ordem.desc ? 'descending' : 'ascending') : 'none'}
                  className={`px-4 py-2.5 font-medium ${info.numerica ? 'text-right' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => onOrdenar(coluna)}
                    title={info.titulo}
                    className={`inline-flex items-center gap-1 rounded-md uppercase tracking-wide transition-colors hover:text-ink ${ativa ? 'text-ink' : ''} ${info.numerica ? 'flex-row-reverse' : ''}`}
                  >
                    {info.rotulo}
                    <span aria-hidden="true" className={`text-[10px] ${ativa ? '' : 'opacity-40'}`}>
                      {ativa ? (ordem.desc ? '▼' : '▲') : '↕'}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ofertas.map((o) => (
            <tr key={o.offerId} className="border-b border-line/60 last:border-0 hover:bg-ink/5">
              <th scope="row" className="px-4 py-2.5 text-left font-normal">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-ink" title={o.name}>
                    {o.name}
                  </span>
                  {mostrarFunil ? (
                    <Badge tone={o.kind === 'expirados' ? 'critical' : 'accent'} className="shrink-0">
                      {ROTULO_DO_FUNIL[o.kind]}
                    </Badge>
                  ) : null}
                </span>
                <span className="block text-xs text-ink-subtle">
                  {PROVIDER_LABEL[o.provider]} · {formatBRLFromCents(o.priceCents)}
                </span>
              </th>
              <td data-numeric="true" className="px-4 py-2.5 text-right text-ink">
                {formatCount(o.enteredCount)}
              </td>
              <td data-numeric="true" className="px-4 py-2.5 text-right font-medium text-ink">
                {formatCount(o.recoveredCount)}
              </td>
              <td data-numeric="true" className="px-4 py-2.5 text-right text-ink-muted">
                {o.enteredCount >= MINIMO_PARA_TAXA ? formatRatio(o.recoveredCount, o.enteredCount) : VALOR_AUSENTE}
              </td>
              <td data-numeric="true" className="px-4 py-2.5 text-right text-ink">
                {o.recoveredAmountCents > 0 ? formatBRLFromCents(o.recoveredAmountCents) : VALOR_AUSENTE}
              </td>
              <td data-numeric="true" className="px-4 py-2.5 text-right text-ink">
                {o.openAmountCents > 0 ? formatBRLFromCents(o.openAmountCents) : VALOR_AUSENTE}
              </td>
              <td data-numeric="true" className="px-4 py-2.5 text-right text-ink-muted">
                {formatCount(o.openCount)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-line bg-elevated text-xs">
            <th scope="row" className="px-4 py-2.5 text-left font-medium text-ink-muted">
              Total do que está filtrado ({formatCount(total.length)} {total.length === 1 ? 'oferta' : 'ofertas'})
            </th>
            <td data-numeric="true" className="px-4 py-2.5 text-right font-medium text-ink">
              {formatCount(soma.entraram)}
            </td>
            <td data-numeric="true" className="px-4 py-2.5 text-right font-medium text-ink">
              {formatCount(soma.recuperados)}
            </td>
            <td data-numeric="true" className="px-4 py-2.5 text-right text-ink-muted">
              {formatRatio(soma.recuperados, soma.entraram)}
            </td>
            <td data-numeric="true" className="px-4 py-2.5 text-right font-medium text-ink">
              {formatBRLFromCents(soma.recuperado)}
            </td>
            <td data-numeric="true" className="px-4 py-2.5 text-right font-medium text-ink">
              {formatBRLFromCents(soma.parado)}
            </td>
            <td data-numeric="true" className="px-4 py-2.5 text-right text-ink-muted">
              {formatCount(soma.abertos)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Cartoes({ ofertas, mostrarFunil }: { readonly ofertas: readonly OfferSummary[]; readonly mostrarFunil: boolean }): JSX.Element {
  return (
    <ul className="flex flex-col gap-2 md:hidden">
      {ofertas.map((o) => (
        <li key={o.offerId} className="rounded-xl border border-line bg-surface px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">{o.name}</p>
              <p className="text-xs text-ink-subtle">
                {PROVIDER_LABEL[o.provider]} · {formatBRLFromCents(o.priceCents)}
              </p>
            </div>
            {mostrarFunil ? <Badge tone={o.kind === 'expirados' ? 'critical' : 'accent'}>{ROTULO_DO_FUNIL[o.kind]}</Badge> : null}
          </div>
          <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <Par rotulo="Entraram" valor={formatCount(o.enteredCount)} />
            <Par rotulo="Recuperados" valor={formatCount(o.recoveredCount)} />
            <Par rotulo="Taxa" valor={o.enteredCount >= MINIMO_PARA_TAXA ? formatRatio(o.recoveredCount, o.enteredCount) : VALOR_AUSENTE} />
            <Par rotulo="Recuperado" valor={o.recoveredAmountCents > 0 ? formatBRLFromCents(o.recoveredAmountCents) : VALOR_AUSENTE} />
            <Par rotulo="Parado" valor={o.openAmountCents > 0 ? formatBRLFromCents(o.openAmountCents) : VALOR_AUSENTE} />
            <Par rotulo="Em aberto" valor={formatCount(o.openCount)} />
          </dl>
        </li>
      ))}
    </ul>
  );
}

function Par({ rotulo, valor }: { readonly rotulo: string; readonly valor: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{rotulo}</dt>
      <dd className="truncate tabular-nums text-ink">{valor}</dd>
    </div>
  );
}
